import { throwError as observableThrowError, Observable, Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Http, Headers, Response, URLSearchParams } from '@angular/http';
import { each, extend, clone, find as _find, filter as _filter, uniq, isEmpty } from 'lodash';
import { environment } from '../../environments/environment';
import * as moment from 'moment';
import * as fileSaver from 'file-saver';

import { Serializer } from './serializer';
import { Resource } from './resource';
import { FilterValues } from './filter';
import { FilterValuesSerializer } from './filter.serializer';
import { IServerSideGetRowsParams } from 'ag-grid-community';
import { _getComponentHostLElementNode } from '@angular/core/src/render3/instructions';

export class ResourceService<T extends Resource> {
  baseUrl = environment.serverUrl;
  appVersion = environment.applicationVersion;
  public nextUri: any;
  public previousUri: any;
  public count: any;
  public unreadCount: any;
  resourceReq!: Subscription;
  mockEndpoint = false;
  mockSearchKeys = ['name'];
  resourceUrl = '';
  search = '';
  filters: { [key: string]: any } = {};

  constructor(
    protected http: Http,
    protected endpoint: string,
    protected serializer: Serializer
  ) {
    this.resourceUrl = this.baseUrl + this.endpoint;
    if (this.endpoint.includes('LOCAL:')) {
      let parts = this.endpoint.split('LOCAL:');
      if (parts) { this.resourceUrl = parts[parts.length - 1]; }
    }
  }

  exportGrid(
    type: string,
    included: string[] = [],
    excluded: string[] = [],
    filters: { [key: string]: string } = {},
    search = '',
    summary = false,
    images = true,
  ): Observable<Response> {
    const url = `${this.baseUrl}export/`;

    const params = new URLSearchParams();
    const _filters = this.buildFilters(filters);
    if (filters && !isEmpty(filters)) { params.set('filters', _filters); }
    if (search) { params.set('search', search); }

    const payload = {
      [type]: included,
      [`excluded_${type}`]: excluded,
      summary_only: summary,
      include_images: images,
    };

    return this.http.post(url, payload, { headers: this.requestHeaders(), search: params });
  }

  exportGridLaserfiche(
    included: string[] = [],
    excluded: string[] = [],
    filters: { [key: string]: string } = {},
    search = '',
    allSelected: boolean
  ): Observable<Response> {
    const url = `${this.baseUrl}pandas-export/`;

    const params = new URLSearchParams();
    const _filters = this.buildFilters(filters);
    if (filters && !isEmpty(filters)) { params.set('filters', _filters); }
    if (search) { params.set('search', search); }

    const payload = {
      include: included,
      exclude: excluded,
      include_images: true
    };
    if (allSelected) { delete payload['include']; }

    let headers = this.requestHeaders(undefined, {
      'X-EXPORT-FILENAME': 'ruckit-tickets',
      'X-EXPORT-ACCEPT': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    return this.http.post(url, payload, { headers: headers, search: params });
  }

  exportGridPandas(
    included: string[] = [],
    excluded: string[] = [],
    filters: { [key: string]: string } = {},
    search = '',
    allSelected: boolean,
    fields: string[] = []
  ): Observable<Response> {
    const url = `${this.baseUrl}pandas-export/`;
    const params = new URLSearchParams();
    const _filters = this.buildFilters(filters);
    let headers = this.requestHeaders();
    if (filters && !isEmpty(filters)) { params.set('filters', _filters); }
    if (search) { params.set('search', search); }

    const payload = {
      include: included,
      exclude: excluded,
      include_images: true,
      fields: fields
    };

    if (!fields || !fields.length) {
      delete payload['fields'];
    } else {
      headers = this.requestHeaders(undefined, {
        'X-EXPORT-FILENAME': 'ruckit-tickets',
        'X-EXPORT-ACCEPT': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }
    if (allSelected) { delete payload['include']; }

    return this.http.post(url, payload, { headers: headers, search: params });
  }

  getValuesForFieldQuery(field: string, value = ''): Observable<string[]> {
    const apiField = field.split(/(?=[A-Z])/).map(s => s.toLowerCase()).join('_');
    const param = `${apiField}__icontains`;

    const query = { [param]: value };
    return this.list(query).pipe(
      map(tickets => {
        return tickets.map(ticket => ticket[field] ? ticket[field] : '');
      }),
      map(fields => {
        return fields.filter(f => f !== '');
      }),
      map(fields => {
        return uniq(fields);
      }),
    );
  }

  list(query?: any): Observable<T[]> {
    const params: URLSearchParams = new URLSearchParams();
    if (query) {
      Object.keys(query).forEach((key) => {
        if (typeof query[key] !== 'undefined' && query[key] && query[key].toString) {
          params.set(key, query[key].toString());
        }
      });
    }

    return this.http.get(this.resourceUrl, {
      headers: this.requestHeaders(),
      search: params
    }).pipe(
      map(res => this.captureMetaData(res)),
      map(data => this.filterLocally(data, params.paramsMap)),
      map(data => this.paginateLocally(data, params.paramsMap)),
      map(data => this.convertData(data)),
      catchError((res: Response) => this.handleError(res))
    );
  }

  get(id?: string): Observable<T> {
    const resourceUrl = id ? `${this.resourceUrl}${id}/` : `${this.resourceUrl}`;

    return this.http.get(resourceUrl, {
      headers: this.requestHeaders()
    }).pipe(
      map(res => this.captureMetaData(res)),
      map(data => this.convertRecord(data)),
      catchError((res: Response) => this.handleError(res))
    );
  }

  save(model: any): Observable<T> {
    const resourceUrl = this.resourceUrl;

    model = clone(model);
    model = <T>this.serializer.toJson(model);

    if (!model.id) {
      return this.http.post(resourceUrl, model, {
        headers: this.requestHeaders()
      }).pipe(
        map(res => this.convertRecord(res))
      );
    } else {
      return this.http.put(`${resourceUrl}${model.id}/`, model, {
        headers: this.requestHeaders()
      }).pipe(
        map(res => this.convertRecord(res))
      );
    }
  }

  listNext(): Observable<T[]> | null {
    if (this.nextUri) {
      return this.http.get(this.nextUri, {
        headers: this.requestHeaders()
      }).pipe(
        map(res => this.captureMetaData(res)),
        map(data => this.convertData(data)),
        catchError((res: Response) => this.handleError(res))
      );
    } else {
      return null;
    }
  }

  remove(model: any) {
    const resourceUrl = this.resourceUrl;
    const id = typeof model === 'string' ? model : model.id;
    return this.http.delete(`${resourceUrl}${id}/`, {
      headers: this.requestHeaders()
    });
  }

  listFilters(slug: string, query?: any): Observable<T[]> {
    const filtersUrl = `${this.resourceUrl}${slug}/`;

    let params: URLSearchParams = new URLSearchParams();
    params.set('page_size', '6');
    if (query && query['search']) {
      params.set('search', query['search']);
    }

    return this.http.get(filtersUrl, {
      headers: this.requestHeaders(),
      search: params
    }).pipe(
      map(res => this.captureMetaData(res)),
      map(res => this.convertData(res)),
      catchError((res: Response) => this.handleError(res))
    );
  }

  getFilterValues(): Observable<FilterValues> {
    const filtersUrl = `${this.resourceUrl}filters/`;

    return this.http.get(filtersUrl, {
      headers: this.requestHeaders()
    }).pipe(
      map(data => this.convertFilterValues(data)),
      map(data => {
        Object.keys(data).forEach(key => {
          data[key] = data[key].filter(value => value !== '');
        });
        return data;
      }),
      catchError((res: Response) => this.handleError(res))
    );
  }

  convertFilterValues(filters: any): FilterValues {
    try {
      filters = filters.json();
    } catch (err) {
      throw err;
    }
    return new FilterValuesSerializer().fromJson(filters);
  }

  captureMetaData(res: any): any {
    let json = res.json();
    this.nextUri = json.next;
    this.previousUri = json.previous;
    this.count = json['count'] || json['results'] && json['results'].length;
    this.unreadCount = json['unread_count'] || 0;
    this.mockEndpoint = json['mock'];
    this.mockSearchKeys = json['mockSearchKeys'];
    return json.results || json;
  }

  public convertData(data: any): T[] {
    return data && data.map((item: any) => this.serializer.fromJson(item));
  }

  convertRecord(data: any): T {
    try {
      data = data.json();
    } catch (err) {
      // Ignore
    }
    return <T>this.serializer.fromJson(data);
  }

  removeNulls(obj: any) {
    Object.keys(obj).forEach(key => {
      if (obj[key] && typeof obj[key] === 'object') {
        this.removeNulls(obj[key]);
      } else if (obj[key] == null) {
        delete obj[key];
      }
    });

    return obj;
  }

  private filterLocally(data: any, params: any): T[] {
    const ignoredKeys = ['ordering', 'filters', 'page', 'page_size'];
    if (this.endpoint.includes('LOCAL:') || this.mockEndpoint) {
      params.forEach((values: any, key: any) => {
        if (values && values.length && !ignoredKeys.includes(key)) {
          data = _filter(data, (o) => {
            if (o.hasOwnProperty(key)) {
              let value = values.join(',');
              let property = o[key];
              return property.toLowerCase() === value.toLowerCase();
            } else if (key === 'search') {
              let filter = false;
              for (const mockKey of this.mockSearchKeys) {
                if (o.hasOwnProperty(mockKey)) {
                  let value = values.join(',');
                  let property = o[mockKey];
                  filter = property.toLowerCase() === value.toLowerCase();
                  if (filter) { break; }
                }
              }
              return filter;
            } else {
              return true;
            }
          });
        } else if (values && values.length && key === 'filters') {
          let _values = clone(values);
          _values.filter(Boolean).forEach((value: any) => {
            value = value.replace(/\(|\)/g, '');
            let [_key, _value] = value.split('=');
            data = _filter(data, (o) => {
              if (o.hasOwnProperty(_key)) {
                let property = o[_key];
                return property.toLowerCase() === _value.toLowerCase();
              }

              return;
            });
          });
        }
      });
      this.count = data.length;
    }

    return data;
  }

  private paginateLocally(data: any, params: any): T[] {
    if (this.endpoint.includes('LOCAL:') || this.mockEndpoint) {
      this.count = clone(data.length);
      let _params: { [key: string]: any } = {};
      params.forEach((values: any, key: any) => {
        if (values && values.length) {
          _params[key] = values.join(',');
        }
      });
      if ((_params['page'] && _params['page'] !== '1') || !_params['page']) {
        _params['recordsToSkip'] = (_params['page'] - 1) * _params['page_size'];
      } else {
        _params['recordsToSkip'] = 0;
      }
      data = data.slice(
        _params['recordsToSkip'],
        _params['recordsToSkip'] + _params['page_size']
      );
    }

    return data;
  }

  private mergeFilters(filters: any): string {
    return filters.map((filter: any) => {
      if (filter.multiple && filter.values) {
        return filter.values.map((value: any) => {
          const _value = [filter.key, value].join('=');
          return `(${_value})`;
        }).filter(Boolean).join('|');
      } else if (filter.values) {
        let values = filter.values;
        if (values === true) { values = 'True'; }
        if (values === false) { values = 'False'; }
        const _value = [filter.key, values].join('=');
        return `(${_value})`;
      }
    }).filter(Boolean).join('&');
  }

  requestHeaders(xhr?: XMLHttpRequest, customHeaders: { [key: string]: any } | null = null): Headers {
    const headers = new Headers();
    const headersObj: { [key: string]: any } = {};
    if (!xhr) {
      extend(headersObj, {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // 'app-version': this.appVersion,
        // 'app-name': this.appVersion
      });
    }

    const userValue = localStorage.getItem('currentUser');
    const currentUser = userValue ? JSON.parse(userValue) : null;
    if (currentUser && currentUser.token) {
      const token = currentUser && currentUser.token;
      const tokenString = 'Token ' + token;
      headersObj['Authorization'] = tokenString;
    }

    each(headersObj, (value: string, header: string) => {
      if (xhr) {
        xhr.setRequestHeader(header, value);
      } else {
        headers.append(header, value);
      }
    });

    if (customHeaders) {
      each(customHeaders, (value: string, header: string) => {
        if (xhr) {
          xhr.setRequestHeader(header, value);
        } else {
          headers.append(header, value);
        }
      });
    }

    return headers;
  }

  public handleError(error: Response | any) {
    return observableThrowError(this.parseErrors(error));
  }

  private parseErrors(err: any) {
    let errors: any[] = [];
    if (err.status >= 500) {
      errors.push(err.statusText);
    } else if (typeof err._body === 'string') {
      try {
        const body = JSON.parse(err._body);
        if (body.detail) {
          errors.push(body.detail);
        } else {
          errors = this.rescurseErrorObject(body, errors);
        }
      } catch (e) { }
    } else {
      errors.push(err);
    }
    return errors;
  }

  private rescurseErrorObject(obj: any, errors: any) {
    each(obj, (msg, key) => {
      if (Array.isArray(msg)) {
        errors = errors.concat(msg.map(err => (key === 'non_field_errors' ? '' : key.replace(/_/g, ' ') + ': ') + err));
      } else if (typeof msg === 'string') {
        errors.push((key === 'non_field_errors' ? '' : key.replace(/_/g, ' ') + ': ') + msg);
      } else if (typeof msg === 'object') {
        errors = this.rescurseErrorObject(msg, errors);
      }
    });
    return errors;
  }

  unCamelCase(value: string): string {
    return value && value.split(/(?=[A-Z])/).map(s => s.toLowerCase()).join('_');
  }

  /**
   * Requests rows from the server-side API for use in AG Grid tables.
   *
   * @param params {IServerSideGetRowsParams} An object containing two callbacks
   * (success and failure) and a request object with details that row the grid
   * is looking for.
   */
  getRows(params: IServerSideGetRowsParams): void {
    const { startRow, endRow, sortModel, filterModel } = params.request;
    const createOrderField = (model: any): string => {
      const { sort, colId } = model;
      const field = this.unCamelCase(colId);

      return sort === 'asc' ? field : `-${field}`;
    };

    /**
     * Generate / calculate pagination parameters
     */
    const pageSize = endRow - startRow;
    const page = Math.floor(endRow / pageSize);
    const ordering = sortModel.length > 0 ? sortModel.map(createOrderField).join(',') : '-created_at';

    /**
     * Generate request params based on pg grid server data model
     */
    let requestParams = new URLSearchParams();
    requestParams.set('page_size', pageSize.toString());
    requestParams.set('page', page.toString());
    requestParams.set('ordering', ordering);
    requestParams.set('search', this.search);
    if (this.resourceUrl.includes('tickets')) { requestParams.set('include_ticket_metas', 'True'); }

    /**
     * Build the query filters based on the filter model returned from AG Grid.
     */
    const combinedFilters = { ...this.filters, ...filterModel };
    if (combinedFilters.image && combinedFilters.image.values) {
      requestParams.set('image__isempty', combinedFilters.image.values[0]);
      delete combinedFilters.image;
    }
    const filters = this.buildFilters(combinedFilters);
    if (filters) { requestParams.set('filters', filters); }

    /**
     * If an exsiting request is in progress, unsubscribe from it to cancel the
     * request and avoid latent returns.
     */
    if (this.resourceReq && typeof this.resourceReq.unsubscribe === 'function') {
      this.resourceReq.unsubscribe();
    }

    /**
     * Request the resource with the request parameters we've built up.
     *
     * Here, we bind the request to the `resourceReq` property so that we can
     * manage and control the subscription when necessary.
     */

    // Cast to any since the gridApi property is set to private for some reason on `params.parentNode`
    const parent = <any>params.parentNode;
    parent.gridApi.hideOverlay();

    this.resourceReq = this.http.get(this.resourceUrl, {
      headers: this.requestHeaders(),
      search: requestParams
    }).subscribe(response => {
      const { results, count } = response.json();
      const records: T[] = results.map((record: any) => this.serializer.fromJson(record));

      if (records.length <= 0) {
        parent.gridApi.showNoRowsOverlay();
      }

      params.successCallback(records, count);
    }, () => {
      params.failCallback();
    });
  }

  /**
   * Processes the applied filters with consideration for the filter type,
   * format, binding operator, and multiple conditions.
   *
   * @param {any} filterModel The set of filters to process
   *
   * @returns {string} Conditions for use with the API's `filters` query param
   */
  buildFilters(filterModel: any): string {
    let params: string[] = [];
    const booleanColumns: string[] = ['invoiced', 'billable', 'verified'];

    Object.keys(filterModel).forEach(key => {
      const filter = filterModel[key];
      const isBoolean = booleanColumns.includes(key);

      let name = key === 'image' ? `${this.unCamelCase(key)}__isempty` : isBoolean ? this.unCamelCase(key) : `${this.unCamelCase(key)}__in`;
      let values = '';

      if (filter.filterType === undefined) {
        const _params = this.processConditionalFilters(key, filter);
        const operator = filter.operator === 'AND' ? '&' : '|';
        params.push(_params.filter(Boolean).join(operator));
      } else if (filter.filterType === 'date') {
        const _params = this.buildDateFilter(key, filter);
        params.push(_params.filter(Boolean).join('|'));
      } else {
        values = filter.values && filter.values.join(',');
        if (values.includes('-- Blank --')) {
          values = values.replace(/-- Blank --,*/, '');
          values = values.replace(/^\,/, '');
          values = values.concat(',');
          values = values.replace(/\,{2,}/, ',');
        }
        if (values) { params.push(`(${name}=${values})`); }
      }
    });

    return params && params.length ? `${params.filter(Boolean).join('&')}` : '';
  }

  /**
   * Processes the date-type filters from the `filter` object and returns each
   * condition's parameter(s).
   *
   * A switch evaluates the `filter.type` property and determines what format
   * and modifier to use when building the query parameters.
   *
   * @param {string} key The query key to use for the filter argument
   * @param {any} filter The filter to process
   *
   * @returns {string[]} Array of prepared query parameters
   */
  buildDateFilter(key: string, filter: any): string[] {
    let params: string[] = [];
    let name = `${this.unCamelCase(key)}`;
    let dateType = key === 'createdAt' ? 'datetime' : 'date';
    let dateFrom = filter.dateFrom && moment(filter.dateFrom, 'YYYY-MM-DD').toDate();
    let dateTo = filter.dateTo && moment(filter.dateTo, 'YYYY-MM-DD').toDate();
    let value = dateType === 'datetime' ? dateFrom.toISOString() : dateFrom;
    let valueArray: any[];
    let notEqual = false;

    switch (filter.type) {
      case 'equals':
        name = `${this.unCamelCase(key)}__range`;
        dateFrom.setHours(0, 0, 0, 0);
        dateTo = clone(dateFrom);
        dateTo.setHours(23, 59, 59, 999);
        valueArray = dateType === 'datetime' ?
                     [ dateFrom.toISOString(), dateTo.toISOString() ] :
                     [ moment(dateFrom).format('YYYY-MM-DD'), moment(dateTo).format('YYYY-MM-DD') ];
        value = valueArray.filter(Boolean).join(',');
        break;
      case 'greaterThan':
        name = `${this.unCamelCase(key)}__gte`;
        dateFrom.setHours(0, 0, 0, 0);
        value = dateType === 'datetime' ?
          dateFrom.toISOString() : moment(dateFrom).format('YYYY-MM-DD');
        break;
      case 'lessThan':
        name = `${this.unCamelCase(key)}__lte`;
        dateFrom.setHours(23, 59, 59, 999);
        value = dateType === 'datetime' ?
          dateFrom.toISOString() : moment(dateFrom).format('YYYY-MM-DD');
        break;
      case 'notEqual':
        name = `${this.unCamelCase(key)}__range`;
        dateFrom.setHours(0, 0, 0, 0);
        dateTo = clone(dateFrom);
        dateTo.setHours(23, 59, 59, 999);
        valueArray = dateType === 'datetime' ?
                     [ dateFrom.toISOString(), dateTo.toISOString() ] :
                     [ moment(dateFrom).format('YYYY-MM-DD'), moment(dateTo).format('YYYY-MM-DD') ];
        value = valueArray.filter(Boolean).join(',');
        notEqual = true;
        break;
      case 'inRange':
        name = `${this.unCamelCase(key)}__range`;
        dateFrom.setHours(0, 0, 0, 0);
        dateTo.setHours(23, 59, 59, 999);
        valueArray = dateType === 'datetime' ?
                     [ dateFrom.toISOString(), dateTo.toISOString() ] :
                     [ moment(dateFrom).format('YYYY-MM-DD'), moment(dateTo).format('YYYY-MM-DD') ];
        value = valueArray.filter(Boolean).join(',');
        break;
    }

    if (value) { params.push(`${notEqual ? '~' : ''}(${name}=${value})`); }

    return params;
  }

  /**
   * Processes the conditions on the `filter` object and joins each condition's
   * parameters with `&` in the event that there is more than one.
   *
   * @param {string} key The query key to use for the filter argument
   * @param {any} filter The filter to process
   *
   * @returns {string[]} Array of prepared query parameters
   */
  processConditionalFilters(key: string, filter: any): string[] {
    let params: string[] = [];
    if (filter.condition1) {
      const param = this.buildDateFilter(key, filter.condition1).filter(Boolean).join('&');
      params.push(param);
    }
    if (filter.condition2) {
      const param = this.buildDateFilter(key, filter.condition2).filter(Boolean).join('&');
      params.push(param);
    }

    return params;
  }

  saveFile(response: any) {
    const blob = new Blob([response._body], { type: 'text/xml' });
    fileSaver.saveAs(blob, 'ruckit-ticket-manager.qwc');
  }
}
